if (!customElements.get('personalizer-favorite-empty-3')) {
class PersonalizerFavoriteEmpty3 extends HTMLElement {
  connectedCallback() {
    this.wrapper = this.querySelector('.pfe3-scratch-wrapper');
    this.canvas = this.querySelector('.pfe3-scratch-canvas');
    if (!this.wrapper || !this.canvas) return;

    this.ctx = this.canvas.getContext('2d');
    this.isScratching = false;
    this.overlayTriggered = false;
    this.moveCount = 0;

    this.initCanvas = this.initCanvas.bind(this);
    this.onResize = this.initCanvas;

    this.bindCanvasEvents();
    this.bindStepButtons();

    window.addEventListener('load', this.initCanvas);
    window.addEventListener('resize', this.onResize);

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0) {
              this.initCanvas();
            }
          });
        },
        { threshold: 0.1 }
      );
      this.io.observe(this.wrapper);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('load', this.initCanvas);
    if (this.io) this.io.disconnect();
  }

  initCanvas() {
    const rect = this.wrapper.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const cssWidth = rect.width;
    const cssHeight = rect.height;
    this.canvas.width = Math.round(cssWidth * dpr);
    this.canvas.height = Math.round(cssHeight * dpr);
    this.canvas.style.width = cssWidth + 'px';
    this.canvas.style.height = cssHeight + 'px';
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const grad = this.ctx.createLinearGradient(0, 0, cssWidth, cssHeight);
    grad.addColorStop(0, '#D4BA8A');
    grad.addColorStop(0.4, '#C5A467');
    grad.addColorStop(0.6, '#B89A55');
    grad.addColorStop(1, '#D4BA8A');
    this.ctx.fillStyle = grad;
    this.ctx.fillRect(0, 0, cssWidth, cssHeight);

    this.ctx.strokeStyle = 'rgba(255,255,255,0.07)';
    this.ctx.lineWidth = 1;
    for (let i = -cssHeight; i < cssWidth + cssHeight; i += 12) {
      this.ctx.beginPath();
      this.ctx.moveTo(i, 0);
      this.ctx.lineTo(i + cssHeight, cssHeight);
      this.ctx.stroke();
    }

    this.ctx.fillStyle = 'rgba(255,255,255,0.95)';
    this.ctx.font = '700 13px "DM Sans", sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText('\u2726  SCRATCH TO REVEAL  \u2726', cssWidth / 2, cssHeight / 2 - 8);
    this.ctx.fillStyle = 'rgba(255,255,255,0.6)';
    this.ctx.font = '400 11px "DM Sans", sans-serif';
    this.ctx.fillText('Use your finger or mouse', cssWidth / 2, cssHeight / 2 + 12);
  }

  getPos(e) {
    const rect = this.canvas.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: cx - rect.left, y: cy - rect.top };
  }

  scratch(x, y) {
    this.ctx.globalCompositeOperation = 'destination-out';
    this.ctx.beginPath();
    this.ctx.arc(x, y, 20, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.globalCompositeOperation = 'source-over';
    this.moveCount++;
    if (this.moveCount % 6 === 0 && this.checkPercent() > 0.5) this.triggerOverlay();
  }

  checkPercent() {
    const data = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height).data;
    let clear = 0;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] === 0) clear++;
    }
    return clear / (data.length / 4);
  }

  triggerOverlay() {
    if (this.overlayTriggered) return;
    this.overlayTriggered = true;
    this.isScratching = false;
    this.canvas.style.transition = 'opacity 0.4s ease';
    this.canvas.style.opacity = '0';

    setTimeout(() => {
      const step = this.closest('.personalizer__step');
      if (!step) return;
      const content = step.querySelector('.personalizer__step-content');
      const action = step.querySelector('.personalizer__step-action');
      if (content) content.classList.add('pfe3-blurred');
      if (action) action.classList.add('pfe3-blurred');

      const overlay = step.querySelector('.pfe3-overlay');
      if (overlay) {
        overlay.classList.add('pfe3-overlay--visible');
        overlay.scrollIntoView({ block: 'center' });
      }
    }, 1500);
  }

  endScratch() {
    this.isScratching = false;
    if (this.checkPercent() > 0.5) this.triggerOverlay();
  }

  bindCanvasEvents() {
    const canvas = this.canvas;
    canvas.addEventListener('mousedown', (e) => {
      this.isScratching = true;
      const p = this.getPos(e);
      this.scratch(p.x, p.y);
    });
    canvas.addEventListener('mousemove', (e) => {
      if (!this.isScratching) return;
      const p = this.getPos(e);
      this.scratch(p.x, p.y);
    });
    canvas.addEventListener('mouseup', () => this.endScratch());
    canvas.addEventListener('mouseleave', () => {
      if (this.isScratching) this.endScratch();
    });
    canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.isScratching = true;
      const p = this.getPos(e);
      this.scratch(p.x, p.y);
    });
    canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (!this.isScratching) return;
      const p = this.getPos(e);
      this.scratch(p.x, p.y);
    });
    canvas.addEventListener('touchend', (e) => {
      e.preventDefault();
      this.endScratch();
    });
  }

  bindStepButtons() {
    const step = this.closest('.personalizer__step');
    const scope = step || document;

    scope.querySelectorAll('.js-pfe3-skip').forEach((btn) => {
      btn.addEventListener('click', () => {
        const s = btn.closest('.personalizer__step');
        if (!s) return;
        const content = s.querySelector('.personalizer__step-content');
        const action = s.querySelector('.personalizer__step-action');
        if (content) content.classList.remove('pfe3-blurred');
        if (action) action.classList.remove('pfe3-blurred');
        const overlay = s.querySelector('.pfe3-overlay');
        if (overlay) overlay.classList.remove('pfe3-overlay--visible');
      });
    });

    scope.querySelectorAll('.js-pfe3-cta').forEach((btn) => {
      btn.addEventListener('click', () => {
        const s = btn.closest('.personalizer__step');
        if (!s) return;
        const nextBtn = s.querySelector('.personalizer__step-button, .js-button-atc');
        if (nextBtn) nextBtn.click();
        const content = s.querySelector('.personalizer__step-content');
        const action = s.querySelector('.personalizer__step-action');
        if (content) content.classList.remove('pfe3-blurred');
        if (action) action.classList.remove('pfe3-blurred');
        const overlay = s.querySelector('.pfe3-overlay');
        if (overlay) overlay.classList.remove('pfe3-overlay--visible');
      });
    });
  }
}

customElements.define('personalizer-favorite-empty-3', PersonalizerFavoriteEmpty3);
}
